import React, { useState, useRef, useEffect, RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
// TODO move hooks and helpers to common folder
import { useOutsideClick, useInsideClick } from 'app/utils/hooks';
import { Avatar } from '../avatar';

import s from './styles.module.scss';

type RequestActionProps<T> = {
  className?: string;
  itemsClassName?: string;
  items?: (React.ReactNode | null)[] | null;
  triggerElement?: React.ReactElement<T>;
  children?: React.ReactNode | ((props: { isOpen: boolean }) => React.ReactNode);
  outsideClick?: (isOpenState: boolean) => void;
  shouldCloseOnClickInside?: boolean;
};

export const Dropdown = <T extends Record<string, any>>({
  className,
  triggerElement = <Avatar />,
  children,
  outsideClick,
  itemsClassName,
  shouldCloseOnClickInside = true,
  ...props
}: RequestActionProps<T>) => {
  const actionsToggleRef = useRef<HTMLDivElement>(null);
  const itemsToggleRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useOutsideClick(actionsToggleRef as RefObject<HTMLDivElement>, () => {
    if (outsideClick) outsideClick(isOpen);

    setIsOpen(false);
  });

  useInsideClick(
    itemsToggleRef as RefObject<HTMLDivElement>,
    () => {
      setIsOpen(false);
    },
    shouldCloseOnClickInside,
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const togglePopup = (e: React.MouseEvent<T>) => {
    if (triggerElement.props.onClick) {
      triggerElement.props.onClick(e, !isOpen);
    }

    setIsOpen((prev) => !prev);
  };

  return (
    <div className={cx(s.dropdown, className)} {...props} ref={actionsToggleRef}>
      {React.cloneElement(triggerElement, { ...triggerElement.props, onClick: togglePopup })}
      <div ref={itemsToggleRef} className={cx(s.dropdownItems, itemsClassName, !isOpen && s.dropdownItemsHidden)}>
        {typeof children === 'function' ? children({ isOpen }) : children}
      </div>
    </div>
  );
};
